import React, { useEffect } from 'react';

/*
 * Модалка-форма, которая ВЛЕЗАЕТ в экран.
 *
 * Раньше модалки настроек рисовались как `fixed inset-0` + блок без
 * ограничения высоты: на ноутбуке с небольшим экраном нижняя часть формы
 * и кнопки уходили за край, а общего скролла у страницы под оверлеем нет.
 *
 * Здесь:
 *  - высота окна не больше экрана (100dvh — учитывает панель браузера на
 *    телефоне; где dvh нет — 100vh);
 *  - шапка и кнопки закреплены, они видны всегда;
 *  - на невысоких экранах (до 820px) поджимаются отступы, чтобы форма
 *    помещалась без прокрутки; прокрутка тела — только крайний случай
 *    (очень маленький экран), и она внутри, а не у всего окна.
 */

const SIZE: Record<NonNullable<Props['size']>, string> = {
  md: 'max-w-md',
  lg: 'max-w-xl',
  xl: 'max-w-2xl',
};

interface Props {
  title: string;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  /** Кнопки внизу (обычно «Отмена» + submit). */
  footer: React.ReactNode;
  size?: 'md' | 'lg' | 'xl';
  children: React.ReactNode;
}

export const FormModal: React.FC<Props> = ({ title, onClose, onSubmit, footer, size = 'md', children }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form
        onSubmit={onSubmit}
        className={`relative flex w-full ${SIZE[size]} flex-col rounded-xl bg-white shadow-2xl max-h-[calc(100vh-1.5rem)] supports-[height:100dvh]:max-h-[calc(100dvh-1.5rem)]`}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-3 [@media(min-height:820px)]:px-6 [@media(min-height:820px)]:py-4">
          <h2 className="text-lg font-semibold text-[#2d2c2c]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
            className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-5 py-3 [@media(min-height:820px)]:space-y-4 [@media(min-height:820px)]:px-6 [@media(min-height:820px)]:py-4">
          {children}
        </div>
        <div className="flex shrink-0 justify-end gap-2 rounded-b-xl border-t border-gray-100 bg-white px-5 py-3 [@media(min-height:820px)]:px-6">
          {footer}
        </div>
      </form>
    </div>
  );
};

export default FormModal;
